import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { graphqlRequest } from '../../utils/graphqlClient';
import Footer from '../../components/Footer/Footer';
import {
  THAILAND_GUIDE_DESCRIPTION,
  THAILAND_GUIDE_TITLE,
  buildCountryDescription,
} from '../../seo/metadata';
import {
  buildJsonLdGraph,
  breadcrumbJsonLd,
  touristDestinationJsonLd,
} from '../../seo/jsonLd';
import JsonLdScript from '../../seo/JsonLdScript';
import { SITE_ORIGIN } from '../../seo/constants';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RouteImageResult {
  routes: { routeId: number; routeImageUrl?: string | null }[];
}

interface PricingResult {
  tripWithCriteria: {
    boatId: number;
    availabilityDetails: { price: number; currency: string }[];
  }[];
}

// ── Marine life emoji map ─────────────────────────────────────────────────────

const MARINE_LIFE_EMOJI: Record<string, string> = {
  'Whale shark': '🦈',
  'Manta ray': '🌊',
  'Leopard shark': '🦈',
  'Grey reef shark': '🦈',
  Octopus: '🐙',
  'Ghost pipefish': '🐠',
  Batfish: '🐡',
  Cuttlefish: '🦑',
  Seahorse: '🐟',
  'Moray eel': '🐍',
  Nudibranch: '🐌',
  'Giant grouper': '🐟',
  Barracuda: '🐟',
  Frogfish: '🐸',
  'Giant trevally': '🐟',
};

// ── Static data ───────────────────────────────────────────────────────────────

const DESTINATION_ID = 3;
const SEARCH_TYPE = 1;

const BOATS = [
  {
    boatId: 40,
    slug: 'mv-tapana-catamaran',
    name: 'MV Tapana Catamaran',
    imageUrl:
      'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/b782a2b1-93fd-4495-0630-48530d71e100/public',
    imageAlt: 'MV Tapana Catamaran liveaboard boat',
    routesText: 'South Andaman · North Andaman · Gulf of Thailand',
    description:
      '32m purpose-built luxury catamaran with 11 ensuite cabins for up to 22 guests. Features a rooftop jacuzzi, hydraulic dive lift, and Nitrox. Up to 4 dives per day including night dives.',
  },
  {
    boatId: 42,
    slug: 'aruna-voyager',
    name: 'Aruna Voyager',
    imageUrl:
      'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/486f8de9-0f14-4d71-60a5-6ac8e77f0d00/public',
    imageAlt: 'Aruna Voyager liveaboard boat',
    routesText: 'South Andaman · North Andaman',
    description:
      'Premium liveaboard covering both North and South Andaman — from Richelieu Rock and the Similan Islands to Hin Daeng, Hin Muang, and Koh Haa. Comfortable ensuite cabins, Nitrox, and an experienced local dive crew.',
  },
];

const ROUTES = [
  {
    routeId: 7,
    name: 'North Andaman',
    badge: 'Most popular',
    fallbackImage:
      'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/ca65d256-f0c8-4a2b-78d2-334f0cc44c00/public',
    imageAlt: 'Similan Islands diving, North Andaman',
    searchSlug: 'north-andaman',
    description:
      "The North Andaman route is Thailand's crown jewel for liveaboard diving. Departing from Khao Lak, boats head out to the Similan Islands — a chain of nine granite islands surrounded by crystal-clear water and some of the healthiest coral reefs in Southeast Asia. The route continues north to Koh Bon (famous for manta ray cleaning stations), Koh Tachai, and finally Richelieu Rock — consistently ranked as one of the top dive sites in the world. This horseshoe-shaped pinnacle is the single best spot in Thailand for whale shark encounters, especially between February and April.",
    stats: {
      season: 'Oct – May',
      visibility: '15 – 30m',
      depthRange: '5 – 30m',
      difficulty: 'All levels',
    },
    diveSites: [
      'Richelieu Rock',
      'Similan Islands',
      'Koh Bon',
      'Koh Tachai',
      'Elephant Head Rock',
    ],
    marineLife: [
      'Whale shark',
      'Manta ray',
      'Leopard shark',
      'Octopus',
      'Ghost pipefish',
      'Batfish',
      'Cuttlefish',
      'Seahorse',
    ],
  },
  {
    routeId: 5,
    name: 'South Andaman',
    badge: null,
    fallbackImage:
      'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/3a98e5ab-ff1f-4cf8-bb4e-9b3aac214600/public',
    imageAlt: 'South Andaman diving, Hin Daeng',
    searchSlug: 'south-andaman',
    description:
      "The South Andaman route takes you to some of Thailand's most dramatic underwater terrain. Starting from Phuket or Krabi, boats head south to Hin Daeng and Hin Muang — twin deep-water pinnacles famous for their towering soft coral walls and regular manta ray visits. This is the route for divers who want a mix of big pelagics and extraordinary reef scenery.",
    stats: {
      season: 'Late Apr – Oct',
      visibility: '10 – 25m',
      depthRange: '5 – 30m',
      difficulty: 'Intermediate+',
    },
    diveSites: [
      'Hin Daeng',
      'Hin Muang',
      'Koh Haa',
      'Phi Phi Islands',
      'Shark Point',
    ],
    marineLife: [
      'Manta ray',
      'Batfish',
      'Cuttlefish',
      'Seahorse',
      'Ghost pipefish',
      'Moray eel',
      'Octopus',
      'Nudibranch',
    ],
  },
  {
    routeId: 49,
    name: 'Koh Tao & Gulf of Thailand',
    badge: null,
    fallbackImage:
      'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/3a98e5ab-ff1f-4cf8-bb4e-9b3aac214600/public',
    imageAlt: 'Koh Tao diving, Gulf of Thailand',
    searchSlug: 'koh-tao',
    description:
      "Koh Tao sits in the Gulf of Thailand and offers a completely different diving experience from the Andaman coast. Chumphon Pinnacle is famous for giant grouper, barracuda schools, and seasonal whale shark encounters. Sail Rock features a dramatic chimney swim-through and is widely considered the Gulf's best single dive site. The Gulf season runs March through August — exactly when the Andaman coast is closed.",
    stats: {
      season: 'Mar – Aug',
      visibility: '10 – 20m',
      depthRange: '5 – 36m',
      difficulty: 'All levels',
    },
    diveSites: [
      'Chumphon Pinnacle',
      'Sail Rock',
      'Southwest Pinnacle',
      'HTMS Sattakut Wreck',
      'Japanese Gardens',
    ],
    marineLife: [
      'Whale shark',
      'Giant grouper',
      'Barracuda',
      'Batfish',
      'Cuttlefish',
      'Frogfish',
      'Seahorse',
      'Nudibranch',
    ],
  },
  {
    routeId: 3,
    name: 'Koh Losin',
    badge: 'Remote & wild',
    fallbackImage:
      'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/3a98e5ab-ff1f-4cf8-bb4e-9b3aac214600/public',
    imageAlt: 'Koh Losin remote pinnacle diving',
    searchSlug: 'koh-losin',
    description:
      "Koh Losin is one of Thailand's most remote and rewarding dive destinations — a small rocky pinnacle rising from the southern Gulf of Thailand, roughly 60km off the coast of Songkhla province. Strong currents bring excellent water clarity and attract large pelagics: grey reef sharks, whale sharks on rare lucky days, and some of the biggest barracuda schools in the country.",
    stats: {
      season: 'Late Jul – Sep',
      visibility: '10 – 25m',
      depthRange: '5 – 30m',
      difficulty: 'Advanced',
    },
    diveSites: [
      'Koh Losin Pinnacle',
      'North Face',
      'Barracuda Point',
      'South Slope',
    ],
    marineLife: [
      'Whale shark',
      'Grey reef shark',
      'Barracuda',
      'Giant trevally',
      'Batfish',
      'Cuttlefish',
      'Seahorse',
      'Nudibranch',
    ],
  },
];

type SeasonRating = 'excellent' | 'good' | 'fair' | 'poor';

const SEASONALITY: { route: string; months: SeasonRating[] }[] = [
  {
    route: 'North Andaman',
    months: [
      'excellent',
      'excellent',
      'excellent',
      'good',
      'poor',
      'poor',
      'poor',
      'poor',
      'poor',
      'fair',
      'good',
      'excellent',
    ],
  },
  {
    route: 'South Andaman',
    months: [
      'poor',
      'poor',
      'poor',
      'fair',
      'good',
      'excellent',
      'excellent',
      'excellent',
      'good',
      'fair',
      'poor',
      'poor',
    ],
  },
  {
    route: 'Koh Losin',
    months: [
      'poor',
      'poor',
      'poor',
      'poor',
      'poor',
      'poor',
      'fair',
      'excellent',
      'excellent',
      'poor',
      'poor',
      'poor',
    ],
  },
  {
    route: 'Koh Tao (Gulf)',
    months: [
      'good',
      'good',
      'excellent',
      'excellent',
      'excellent',
      'good',
      'good',
      'good',
      'fair',
      'poor',
      'poor',
      'fair',
    ],
  },
];

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const SEASON_COLORS: Record<SeasonRating, string> = {
  excellent: 'bg-green-600',
  good: 'bg-green-400',
  fair: 'bg-amber-400',
  poor: 'bg-gray-200',
};

// ── Pricing hook ──────────────────────────────────────────────────────────────

const PRICING_QUERY = `
  query($req: TripSearchRequestInput!) {
    tripWithCriteria(request: $req) {
      boatId
      availabilityDetails { price currency }
    }
  }
`;

function useBoatMinPrice(boatId: number): string {
  const [price, setPrice] = useState<string>('—');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = new Date();
      for (let offset = 0; offset < 12; offset++) {
        if (cancelled) return;
        const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        try {
          const data = await graphqlRequest<PricingResult>(PRICING_QUERY, {
            req: {
              destinationId: DESTINATION_ID,
              month: d.getMonth() + 1,
              year: d.getFullYear(),
              numberOfDiver: 1,
              searchType: SEARCH_TYPE,
            },
          });
          const boat = data.tripWithCriteria?.find(t => t.boatId === boatId);
          const prices = boat?.availabilityDetails
            ?.map(a => a.price)
            .filter(p => p > 0);
          if (prices && prices.length > 0) {
            const min = Math.min(...prices);
            const currency = boat!.availabilityDetails[0].currency ?? 'THB';
            if (!cancelled) setPrice(`${currency} ${min.toLocaleString()}`);
            return;
          }
        } catch {
          // continue to next month
        }
      }
      if (!cancelled) setPrice('—');
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId]);

  return price;
}

// ── Route images hook ─────────────────────────────────────────────────────────

const ROUTE_IMAGES_QUERY = `
  query {
    routes {
      routeId
      routeImageUrl
    }
  }
`;

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-2 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
        {label}
      </p>
      <p className="text-xl font-black text-[#00294A]">{value}</p>
      <p className="text-sm text-gray-500">{sub}</p>
    </div>
  );
}

function RouteCard({
  route,
  imageUrl,
}: {
  route: (typeof ROUTES)[number];
  imageUrl: string;
}) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
      <div className="relative h-56 lg:h-72 overflow-hidden">
        <img
          src={imageUrl}
          alt={route.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#00294A]/80 via-[#00294A]/20 to-transparent" />
        <div className="absolute bottom-6 left-6 lg:left-8">
          {route.badge && (
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
              {route.badge}
            </span>
          )}
          <h3 className="text-2xl lg:text-3xl font-black text-white">
            {route.name}
          </h3>
        </div>
      </div>

      <div className="p-6 lg:p-8">
        <p className="text-gray-500 text-base leading-relaxed max-w-3xl mb-6">
          {route.description}
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            ['Season', route.stats.season],
            ['Visibility', route.stats.visibility],
            ['Depth range', route.stats.depthRange],
            ['Difficulty', route.stats.difficulty],
          ].map(([label, val]) => (
            <div key={label} className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 uppercase tracking-wide font-bold">
                {label}
              </p>
              <p className="text-[#00294A] font-bold mt-1">{val}</p>
            </div>
          ))}
        </div>

        <div className="mb-5">
          <h4 className="font-bold text-[#00294A] text-sm uppercase tracking-wide mb-3">
            Key dive sites
          </h4>
          <div className="flex flex-wrap gap-2">
            {route.diveSites.map(site => (
              <span
                key={site}
                className="bg-blue-50 text-[#005DAB] text-sm font-bold px-3 py-1 rounded-full"
              >
                {site}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h4 className="font-bold text-[#00294A] text-sm uppercase tracking-wide mb-3">
            Marine life to watch for
          </h4>
          <div className="flex gap-6 overflow-x-auto pb-2">
            {route.marineLife.map(species => (
              <div
                key={species}
                className="flex flex-col items-center gap-2 min-w-[64px]"
              >
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <span className="text-2xl" role="img" aria-label={species}>
                    {MARINE_LIFE_EMOJI[species] ?? '🐟'}
                  </span>
                </div>
                <span className="text-xs text-gray-500 text-center whitespace-nowrap">
                  {species}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Link
          to={`/routes/${route.searchSlug}`}
          className="inline-flex items-center gap-2 text-[#005DAB] text-sm font-bold hover:underline"
        >
          View {route.name} trips
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 8h6m-2-2 2 2-2 2" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function BoatCard({ boat }: { boat: (typeof BOATS)[number] }) {
  const price = useBoatMinPrice(boat.boatId);
  return (
    <Link
      to={`/boat/${boat.slug}`}
      className="block bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow no-underline text-inherit"
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={boat.imageUrl}
          alt={boat.imageAlt}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-6">
        <h3 className="text-lg font-black text-[#00294A] mb-1">{boat.name}</h3>
        <p className="text-sm text-[#005DAB] font-semibold mb-3">
          {boat.routesText}
        </p>
        <p className="text-sm text-gray-500 leading-relaxed mb-4">
          {boat.description}
        </p>
        <p className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-1">
          Starting from
        </p>
        <p className="text-xl font-black text-[#00294A]">{price}</p>
        <p className="text-xs text-gray-400 mt-1">per diver</p>
      </div>
    </Link>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ThailandGuide() {
  const { data: routeImagesData } = useQuery<RouteImageResult>({
    queryKey: ['routeImages'],
    queryFn: () => graphqlRequest<RouteImageResult>(ROUTE_IMAGES_QUERY),
    staleTime: 10 * 60 * 1000,
  });

  const routeImageMap: Record<number, string> = {};
  routeImagesData?.routes.forEach(r => {
    if (r.routeImageUrl) routeImageMap[r.routeId] = r.routeImageUrl;
  });

  const thailandGuideJsonLd = buildJsonLdGraph([
    touristDestinationJsonLd({
      name: 'Thailand',
      url: `${SITE_ORIGIN}/guides/thailand`,
      description: buildCountryDescription('Thailand'),
    }),
    breadcrumbJsonLd([
      { name: 'Home', url: `${SITE_ORIGIN}/` },
      { name: 'Thailand Diving Guide', url: `${SITE_ORIGIN}/guides/thailand` },
    ]),
  ]);

  return (
    <>
      <Helmet>
        <title>{THAILAND_GUIDE_TITLE}</title>
        <meta name="description" content={THAILAND_GUIDE_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_ORIGIN}/guides/thailand`} />
        <JsonLdScript json={thailandGuideJsonLd} />
      </Helmet>

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[60px] flex items-center justify-between">
          <Link to="/">
            <img
              src="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/33a0d3dd-4713-44fc-2792-1237db489400/public"
              alt="Santtai"
              className="h-7"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/destinations/thailand"
              className="text-sm font-semibold text-gray-700 hover:text-[#005DAB] no-underline"
            >
              Destinations
            </Link>
            <a
              href="#boats"
              className="text-sm font-semibold text-gray-700 hover:text-[#005DAB] no-underline"
            >
              Boats
            </a>
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative min-h-[70vh] lg:min-h-[80vh] flex items-end">
        <img
          src="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/58e045f4-397e-4547-90a5-8c5617f55f00/public"
          alt="Whale shark encounter in Thai waters"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#00294A]/90 via-[#00294A]/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-12 lg:pb-20 w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70">
              <li>
                <Link
                  to="/"
                  className="text-white/70 hover:text-white no-underline"
                >
                  Home
                </Link>
              </li>
              <li>/</li>
              <li className="text-white font-semibold">Thailand</li>
            </ol>
          </nav>
          <h1 className="text-4xl lg:text-6xl font-black text-white leading-tight max-w-2xl">
            Liveaboard Diving in Thailand
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-xl leading-relaxed">
            From the whale sharks of Richelieu Rock to the pristine coral
            gardens of the Similan Islands — Thailand offers some of the world's
            most spectacular liveaboard diving.
          </p>
          <Link
            to="/destinations/thailand"
            className="mt-8 inline-flex items-center gap-2 bg-[#005DAB] text-white font-bold px-6 py-3 rounded-xl hover:bg-[#004D8C] transition-colors no-underline"
          >
            Explore Thailand trips
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 9h8m-3-3 3 3-3 3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ── Thailand at a glance ── */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl font-black text-[#00294A] mb-2">
            Thailand at a glance
          </h2>
          <p className="text-gray-500 mb-8">
            Everything you need to plan your liveaboard diving trip.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <StatCard
              label="Best time to visit"
              value="November – April"
              sub="Andaman Sea peak season. Gulf coast: March – August."
            />
            <StatCard
              label="Top dive sites"
              value="Richelieu Rock"
              sub="Similan Islands, Hin Daeng, Hin Muang, Koh Bon, Sail Rock"
            />
            <StatCard
              label="Known for"
              value="Whale sharks & mantas"
              sub="Pristine coral reefs, macro life, dramatic pinnacles, warm water year-round"
            />
            <StatCard
              label="Good to know"
              value="27–30°C water"
              sub="Visibility up to 30m. 3mm wetsuit. Departure ports: Khao Lak, Phuket, Koh Tao."
            />
          </div>
        </div>
      </section>

      {/* ── Popular dive routes ── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl font-black text-[#00294A] mb-2">
            Popular dive routes
          </h2>
          <p className="text-gray-500 mb-10">
            Thailand's liveaboard routes range from the famous Similan Islands
            in the north to the deep pinnacles of the south. Each offers a
            distinct diving experience.
          </p>
          <div className="flex flex-col gap-10">
            {ROUTES.map(route => (
              <RouteCard
                key={route.routeId}
                route={route}
                imageUrl={routeImageMap[route.routeId] ?? route.fallbackImage}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Boats ── */}
      <section id="boats" className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl font-black text-[#00294A] mb-2">
            Liveaboard boats in Thailand
          </h2>
          <p className="text-gray-500 mb-10">
            Compare boats, routes, and prices. All trips include diving
            equipment, meals, and accommodation.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {BOATS.map(boat => (
              <BoatCard key={boat.boatId} boat={boat} />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/destinations/thailand"
              className="inline-flex items-center gap-2 text-[#005DAB] font-bold hover:underline no-underline"
            >
              View all Thailand liveaboards
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 8h6m-2-2 2 2-2 2" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Seasonality ── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl font-black text-[#00294A] mb-2">
            Best time to dive in Thailand
          </h2>
          <p className="text-gray-500 mb-8">
            The Andaman Sea and Gulf of Thailand have different seasons. Here's
            when to go for the best conditions.
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 overflow-x-auto">
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-6 text-sm">
              {(['excellent', 'good', 'fair', 'poor'] as SeasonRating[]).map(
                r => (
                  <div key={r} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${SEASON_COLORS[r]}`} />
                    <span className="capitalize text-gray-600">
                      {r === 'poor'
                        ? 'Monsoon / No trips'
                        : r.charAt(0).toUpperCase() + r.slice(1)}
                    </span>
                  </div>
                ),
              )}
            </div>

            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr>
                  <th className="text-left font-bold text-gray-500 pb-3 pr-4 text-xs uppercase tracking-wide w-32">
                    Route
                  </th>
                  {MONTHS.map(m => (
                    <th
                      key={m}
                      className="text-center font-bold text-gray-500 pb-3 text-xs uppercase tracking-wide"
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SEASONALITY.map(row => (
                  <tr key={row.route}>
                    <td className="font-bold text-[#00294A] py-2 pr-4 whitespace-nowrap">
                      {row.route}
                    </td>
                    {row.months.map((rating, i) => (
                      <td key={i} className="py-2">
                        <div
                          className={`mx-auto w-8 h-6 rounded ${SEASON_COLORS[rating]}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-6 bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-[#00294A] leading-relaxed">
                <strong>Pro tip:</strong> Thailand has diving year-round if you
                know where to look. The North Andaman season (November–April)
                and South Andaman season (late April–October) run back-to-back.
                The Gulf coast (Koh Tao: March–August; Koh Losin: late
                July–September) fills the gaps. There is always a season on
                somewhere in Thailand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 lg:py-28 bg-[#00294A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-black text-white">
            Ready to dive Thailand?
          </h2>
          <p className="mt-4 text-lg text-white/80 leading-relaxed">
            Browse available trips, compare boats and itineraries, and book your
            spot — with flexible payment options.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/destinations/thailand"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#00294A] font-bold px-8 py-4 rounded-xl hover:bg-gray-50 transition-colors no-underline"
            >
              Browse Thailand trips
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 9h8m-3-3 3 3-3 3" />
              </svg>
            </Link>
            <a
              href="mailto:hello@santtai.com"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-bold px-8 py-4 rounded-xl hover:border-white/60 transition-colors no-underline"
            >
              Contact us
            </a>
          </div>
          <p className="mt-6 text-sm text-white/50">
            Book now, pay later · Free cancellation on select trips
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}
